/**
 * Get location time using an IANA timezone string (e.g., "America/New_York")
 * Uses the browser-native Intl API -- zero dependencies, handles DST automatically
 *
 * @param {string} ianaTimezone - IANA timezone identifier
 * @returns {Date|null} Date object representing current time in that timezone, or null on failure
 */
export const getLocationTimeFromTimezone = (ianaTimezone) => {
  if (!ianaTimezone) return null;
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(new Date());
    const get = (type) => parts.find(p => p.type === type)?.value;
    return new Date(
      `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`
    );
  } catch (e) {
    return null;
  }
};

/**
 * Fallback location time estimation using longitude-based offset
 * 
 * @param {Object} coordinates - Location coordinates (lat/lng)
 * @returns {Date} Date object with estimated time for location
 */
export const getEstimatedLocationTime = (coordinates) => {
  try {
    if (!coordinates || !coordinates.lat || !coordinates.lng) {
      return new Date(); // If no coordinates, return local time
    }
    
    const now = new Date();
    
    // Get the current month to help determine DST status
    const currentMonth = now.getMonth() + 1; // 1-12
    const currentDay = now.getDate(); // 1-31
    
    // Improved timezone estimation with better DST handling
    let timezoneOffset = 0; // Default UTC
    let inDST = false;
    
    // London is at approximately 51.5074° N, 0.1278° W
    if (Math.abs(coordinates.lat - 51.5074) < 1 && Math.abs(coordinates.lng - (-0.1278)) < 1) {
      // London is GMT in winter, BST (GMT+1) in summer
      // UK DST: Last Sunday in March to last Sunday in October
      inDST = (currentMonth > 3 && currentMonth < 10) || 
              (currentMonth === 3 && currentDay >= 25) || 
              (currentMonth === 10 && currentDay <= 25);
      timezoneOffset = inDST ? 1 : 0;
    }
    // New York is at approximately 40.7128° N, 74.0060° W
    else if (Math.abs(coordinates.lat - 40.7128) < 1 && Math.abs(coordinates.lng - (-74.0060)) < 1) {
      // New York is UTC-5, UTC-4 during DST
      // US DST: Second Sunday in March to first Sunday in November
      inDST = (currentMonth > 3 && currentMonth < 11) || 
              (currentMonth === 3 && currentDay >= 8) || 
              (currentMonth === 11 && currentDay <= 7);
      timezoneOffset = inDST ? -4 : -5;
    }
    // Tokyo is at approximately 35.6762° N, 139.6503° E
    else if (Math.abs(coordinates.lat - 35.6762) < 1 && Math.abs(coordinates.lng - 139.6503) < 1) {
      // Tokyo doesn't observe DST, always UTC+9
      timezoneOffset = 9;
    }
    // Sydney is at approximately 33.8688° S, 151.2093° E
    else if (Math.abs(coordinates.lat - (-33.8688)) < 1 && Math.abs(coordinates.lng - 151.2093) < 1) {
      // Sydney is UTC+10, UTC+11 during DST
      // Australian DST (Southern Hemisphere): First Sunday in October to first Sunday in April
      // Note that Southern Hemisphere seasons are opposite to Northern Hemisphere
      inDST = (currentMonth >= 10 || currentMonth <= 3) || 
              (currentMonth === 4 && currentDay <= 7);
      timezoneOffset = inDST ? 11 : 10;
    }
    // Rio de Janeiro is at approximately 22.9068° S, 43.1729° W
    else if (Math.abs(coordinates.lat - (-22.9068)) < 1 && Math.abs(coordinates.lng - (-43.1729)) < 1) {
      // Brazil no longer observes DST since 2019, always UTC-3
      timezoneOffset = -3;
    }
    // For other locations, use the longitude-based approximation but with improvements
    else {
      timezoneOffset = Math.round(coordinates.lng / 15);
      
      // Improvements for known timezone regions
      if (coordinates.lng >= -10 && coordinates.lng <= 35) {
        // Europe and Africa general regions
        if (coordinates.lat >= 35) {
          // European adjustments
          if (coordinates.lng < 15) timezoneOffset = 1; // Central European Time
          else if (coordinates.lng < 30) timezoneOffset = 2; // Eastern European Time
          else timezoneOffset = 3; // Moscow Time
        }
      }
      
      // North America adjustments
      if (coordinates.lng <= -55 && coordinates.lng >= -130 && coordinates.lat >= 25) {
        if (coordinates.lng < -115) timezoneOffset = -8; // Pacific Time
        else if (coordinates.lng < -100) timezoneOffset = -7; // Mountain Time
        else if (coordinates.lng < -85) timezoneOffset = -6; // Central Time
        else if (coordinates.lng < -65) timezoneOffset = -5; // Eastern Time
        else timezoneOffset = -4; // Atlantic Time
      }
    }
    
    // Get the current UTC time
    const utcTime = new Date(now.getTime() + now.getTimezoneOffset() * 60 * 1000);
    
    // Apply the estimated timezone offset
    const estimatedLocationTime = new Date(utcTime.getTime() + timezoneOffset * 60 * 60 * 1000);
    
    return estimatedLocationTime;
  } catch (error) {
    console.error('Failed to estimate location time:', error);
    return new Date();
  }
};